import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Monitor, RefreshCw, Power, Sun, Moon, Trash2, Globe, 
  RotateCcw, Loader2, Wifi, WifiOff, ChevronDown, ChevronUp,
  Smartphone, Tv
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FullyKioskControl = ({ pageId }) => {
  const [displays, setDisplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [sending, setSending] = useState({});
  const [urlInputs, setUrlInputs] = useState({});
  const [brightnessInputs, setBrightnessInputs] = useState({});

  useEffect(() => {
    loadDisplays();
    const interval = setInterval(loadDisplays, 30000);
    return () => clearInterval(interval);
  }, [pageId]);

  const loadDisplays = async () => {
    try {
      const response = await axios.get(`${API}/health/displays/page/${pageId}`);
      setDisplays(response.data || []);
    } catch (error) {
      console.error('Failed to load displays:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendCommand = async (displayId, command, params = {}) => {
    setSending(prev => ({ ...prev, [`${displayId}-${command}`]: true }));
    try {
      await axios.post(`${API}/fully/command`, {
        display_id: displayId,
        command: command,
        params: params
      });
      toast.success(`Command "${command}" sent`);
    } catch (error) {
      toast.error(`Failed to send command: ${command}`);
    } finally {
      setSending(prev => ({ ...prev, [`${displayId}-${command}`]: false }));
    }
  };

  const sendCommandToAll = async (command, params = {}) => {
    setSending(prev => ({ ...prev, [`all-${command}`]: true }));
    try {
      await axios.post(`${API}/fully/command/bulk?command=${command}&page_id=${pageId}`);
      toast.success(`Command "${command}" sent to all displays`);
    } catch (error) {
      toast.error(`Failed to send command to all displays`);
    } finally {
      setSending(prev => ({ ...prev, [`all-${command}`]: false }));
    }
  };

  const isSending = (id, cmd) => sending[`${id}-${cmd}`];

  if (loading) return null;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="fully-kiosk-control">
      <CardContent className="p-6">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Tv className="w-5 h-5 text-cyan-400" /> Fully Kiosk Remote Control
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-slate-400 text-sm">{displays.length} display{displays.length !== 1 ? 's' : ''}</span>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>

        {expanded && (
          <div className="mt-4 space-y-4">
            {/* Bulk controls */}
            {displays.length > 0 && (
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider font-semibold">All Displays</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => sendCommandToAll('reload')}
                    disabled={isSending('all', 'reload')}
                    className="bg-cyan-500 hover:bg-cyan-600 h-8"
                    data-testid="bulk-reload-btn"
                  >
                    {isSending('all', 'reload') ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Reload All
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => sendCommandToAll('screenOn')}
                    disabled={isSending('all', 'screenOn')}
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-700 h-8"
                  >
                    <Sun className="w-3 h-3 mr-1" /> Screens On
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => sendCommandToAll('screenOff')}
                    disabled={isSending('all', 'screenOff')}
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-700 h-8"
                  >
                    <Moon className="w-3 h-3 mr-1" /> Screens Off
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => sendCommandToAll('clearCache')}
                    disabled={isSending('all', 'clearCache')}
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-700 h-8"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Clear Cache
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => sendCommandToAll('restart')}
                    disabled={isSending('all', 'restart')}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Restart All
                  </Button>
                </div>
              </div>
            )}

            {/* Per-display controls */}
            {displays.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No displays connected to this page.</p>
            ) : (
              displays.map((display) => (
                <div
                  key={display.display_id}
                  className={`p-4 rounded-lg border ${
                    display.status === 'online'
                      ? 'bg-slate-800/50 border-slate-700'
                      : 'bg-slate-800/30 border-slate-800'
                  }`}
                >
                  {/* Display header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {display.status === 'online' ? (
                        <Wifi className="w-4 h-4 text-green-400" />
                      ) : (
                        <WifiOff className="w-4 h-4 text-red-400" />
                      )}
                      <span className="text-white font-medium text-sm">
                        {display.display_name || display.display_id?.slice(0, 16)}
                      </span>
                      {display.ip_address && display.ip_address !== 'N/A' && (
                        <span className="text-slate-500 text-xs">({display.ip_address})</span>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      display.status === 'online'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {display.status}
                    </span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Button
                      size="sm"
                      onClick={() => sendCommand(display.display_id, 'reload')}
                      disabled={isSending(display.display_id, 'reload')}
                      className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30 h-7 text-xs"
                      data-testid={`reload-${display.display_id}`}
                    >
                      {isSending(display.display_id, 'reload') ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                      Reload
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => sendCommand(display.display_id, 'screenOn')}
                      disabled={isSending(display.display_id, 'screenOn')}
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700 h-7 text-xs"
                    >
                      <Sun className="w-3 h-3 mr-1" /> Screen On
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => sendCommand(display.display_id, 'screenOff')}
                      disabled={isSending(display.display_id, 'screenOff')}
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700 h-7 text-xs"
                    >
                      <Moon className="w-3 h-3 mr-1" /> Screen Off
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => sendCommand(display.display_id, 'clearCache')}
                      disabled={isSending(display.display_id, 'clearCache')}
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700 h-7 text-xs"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Clear Cache
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => sendCommand(display.display_id, 'restart')}
                      disabled={isSending(display.display_id, 'restart')}
                      variant="outline"
                      className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-7 text-xs"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" /> Restart
                    </Button>
                  </div>

                  {/* Load URL */}
                  <div className="flex gap-2 mb-2">
                    <Input
                      type="text"
                      value={urlInputs[display.display_id] || ''}
                      onChange={(e) => setUrlInputs(prev => ({ ...prev, [display.display_id]: e.target.value }))}
                      placeholder="Load URL..."
                      className="bg-slate-900 border-slate-700 text-white text-xs h-7"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        if (urlInputs[display.display_id]) {
                          sendCommand(display.display_id, 'loadUrl', { url: urlInputs[display.display_id] });
                          setUrlInputs(prev => ({ ...prev, [display.display_id]: '' }));
                        }
                      }}
                      disabled={!urlInputs[display.display_id]}
                      className="bg-slate-700 hover:bg-slate-600 h-7 text-xs px-3"
                    >
                      <Globe className="w-3 h-3 mr-1" /> Go
                    </Button>
                  </div>

                  {/* Brightness */}
                  <div className="flex items-center gap-2">
                    <Sun className="w-3 h-3 text-slate-500" />
                    <input
                      type="range"
                      min="0"
                      max="255"
                      value={brightnessInputs[display.display_id] ?? 128}
                      onChange={(e) => setBrightnessInputs(prev => ({ ...prev, [display.display_id]: parseInt(e.target.value) }))}
                      className="flex-1 h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                    <Button
                      size="sm"
                      onClick={() => sendCommand(display.display_id, 'setBrightness', { brightness: brightnessInputs[display.display_id] ?? 128 })}
                      disabled={isSending(display.display_id, 'setBrightness')}
                      className="bg-slate-700 hover:bg-slate-600 h-6 text-xs px-2"
                    >
                      Set
                    </Button>
                    <span className="text-slate-500 text-xs w-8 text-right">
                      {Math.round(((brightnessInputs[display.display_id] ?? 128) / 255) * 100)}%
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FullyKioskControl;
